import { useEffect, useRef, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, ExternalLink, FileText, KeyRound, Pencil, Trash2, Plus, Eye, EyeOff, User, ShieldAlert, ShieldOff, Power, PowerOff } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { WORK_TYPE_BADGE, WORK_TYPE_LABELS, type WorkType } from "@/features/contractors/workTypes";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import { VisibleAmountSum } from "@/components/ui/VisibleAmountSum";
import {
  isSettledProtocol,
  protocolNetValue,
  type ProtocolValueItem,
} from "@/features/deliveries/utils/protocolValue";
import { formatPhone } from "@/utils/formatPhone";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";
import { useAuthStore } from "@/store/auth";
import {
  EntityType,
  getContractorDisplayName,
} from "@/features/contractors/utils";
import { usePageTitle } from "@/hooks/usePageTitle";
import { canManageContractors, canToggleContractorActive } from "@/features/contractors/utils";
import type { ContractorFlowState } from "@/features/contractors/prefill";
import { useDecideAccountRequest } from "@/features/rfq/api/proceedings";
import { CooperationScopeSection } from "@/features/rfq/components/CooperationScopeSection";
import { ContractorHistorySection } from "@/features/contractors/components/ContractorHistorySection";
import { ContractorKnowledgeNotesSection } from "@/features/contractors/components/ContractorKnowledgeNotesSection";
import { ContractorLeavesSection } from "@/features/contractors/components/ContractorLeavesSection";
import { firstApiError, onLeaveLabel, type OnLeaveBrief } from "@/features/contractors/api/leaves";
import { ContractorTodoView } from "@/features/orders/components/ContractorTodoView";
import { usePersistentToggle } from "@/hooks/usePersistentToggle";

/** Sekcja „Terminy (TODO)" w kartotece — zwijana; stan zwinięcia to preferencja
 *  przeglądarki (localStorage, wspólna dla wszystkich firm), zwinięta nie pobiera
 *  danych. */
function TodoPreviewSection({ contractorId }: { contractorId: string }) {
  const [collapsed, setCollapsed] = usePersistentToggle("contractor-detail:todo-collapsed", false);
  return (
    <section className="mb-6">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-expanded={!collapsed}
        className="flex items-center gap-1.5 text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 hover:text-[var(--color-primary)]"
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        Terminy (TODO)
      </button>
      {!collapsed && <ContractorTodoView contractorId={contractorId} />}
    </section>
  );
}
import { ContractorRatingsSection } from "@/features/rfq/components/ContractorRatingsSection";

interface Address {
  street: string;
  building_number: string;
  apartment_number: string | null;
  city: string;
  postal_code: string;
  country: string;
}

interface Contractor {
  id: string;
  entity_type: EntityType;
  name: string;
  short_name: string;
  first_name?: string;
  last_name?: string;
  pesel?: string;
  nip: string;
  regon: string;
  krs: string;
  is_vat_payer: boolean;
  eu_vat_number: string;
  // Nullable in the model — e.g. a firm created by a seed or without registry data.
  registered_address: Address | null;
  correspondence_address: Address | null;
  bank_account_number: string;
  bank_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  notes: string;
  is_active: boolean;
  order_mail_enabled: boolean;
  /** Typy prowadzonych prac z kartoteki (kolejność słownika). */
  work_types?: WorkType[];
  /** Najbliższy urlop firmy w oknie składania zamówienia (dziś + 7 dni). */
  on_leave?: OnLeaveBrief | null;
  display_name?: string;
  tax_identifier?: string;
  tax_identifier_label?: "NIP" | "PESEL";
}

interface ContractorUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  username: string;
  is_active: boolean;
  sms_notifications_enabled: boolean;
  can_see_rates: boolean;
  can_access_rfq: boolean;
  can_access_led_projects: boolean;
}

interface OrderLineItem {
  // null = rate-masked for this viewer (rate_visibility, server-side)
  total_value: string | null;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  priority: string;
  deadline: string | null;
  project_name: string;
  line_items: OrderLineItem[];
}

interface Protocol {
  id: string;
  order: string;
  protocol_number: string;
  order_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  accepted_by_contractor: boolean | null;
  pock_approved_settlement_pct?: string;
  items: ProtocolValueItem[];
  created_at: string;
}

interface Agreement {
  id: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  is_confidential: boolean;
  document_managed_file: string | null;
  document_managed_file_detail: (ManagedFileRef & { original_filename?: string }) | null;
  line_items_count: number;
  requires_insurance: boolean;
  insurance_status: "not_required" | "missing" | "expired" | "expiring_soon" | "valid";
}

interface Invoice {
  id: string;
  invoice_number: string;
  amount_net: string;
  amount_gross: string;
  status: string;
  booking_status: string;
  payment_status: string;
  submitted_at: string;
  order_number: string;
}

const orderStatusLabel: Record<string, string> = {
  sent: "Wysłane", accepted: "Przyjęte", rejected: "Odrzucone",
  in_progress: "W realizacji", in_progress_partial: "Realizacja częściowa",
  submitted_for_acceptance: "Do odbioru", accepted_final: "Zakończone", closed: "Zamknięte",
  cancelled: "Anulowane",
};
const orderStatusColor: Record<string, "gray" | "blue" | "green" | "red" | "yellow"> = {
  sent: "blue", accepted: "blue", rejected: "red", in_progress: "yellow",
  in_progress_partial: "yellow", submitted_for_acceptance: "blue",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

const invoiceStatusLabel: Record<string, string> = {
  submitted: "Przesłana", accepted: "Przyjęta", rejected: "Nieprzyjęta", withdrawn: "Wycofana",
};
const invoiceStatusColor: Record<string, "gray" | "green" | "red" | "blue"> = {
  submitted: "blue", accepted: "green", rejected: "red", withdrawn: "gray",
};

function isAgreementExpired(validUntil: string | null): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

function formatAddress(addr: Address | null): string | null {
  if (!addr) return null;
  const parts = [
    `${addr.street} ${addr.building_number}${addr.apartment_number ? `/${addr.apartment_number}` : ""}`,
    `${addr.postal_code} ${addr.city}`,
  ];
  if (addr.country && addr.country !== "Polska") parts.push(addr.country);
  return parts.join(", ");
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-gray-500 dark:text-gray-400 min-w-[160px]">{label}</span>
      <span className="text-gray-900 dark:text-gray-100 font-medium">{value}</span>
    </div>
  );
}

interface AddUserForm {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  code: string;
  sms_notifications_enabled: boolean;
  can_see_rates: boolean;
  can_access_rfq: boolean;
  can_access_led_projects: boolean;
}

/** Keys rendered as plain text inputs — excludes the boolean flags. */
type AddUserTextField = "first_name" | "last_name" | "email" | "phone_number" | "code";

const emptyAddUserForm = (): AddUserForm => ({
  first_name: "", last_name: "", email: "", phone_number: "", code: "",
  sms_notifications_enabled: false, can_see_rates: true, can_access_rfq: false,
  can_access_led_projects: true,
});

/**
 * Per-contractor switch for order notification emails, on by default.
 *
 * A DB flag rather than a deploy-time list, because the person who decides which
 * subcontractor should or should not receive mail is a director on this page, not
 * whoever can edit environment variables.
 */
function OrderMailToggle({ contractor }: { contractor: Contractor }) {
  const authUser = useAuthStore((s) => s.user);
  // Same gate as rate visibility — the contractor's mail eligibility follows from it.
  const canManage = canManageContractors(authUser);
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiClient.patch(`/contractors/${contractor.id}/`, { order_mail_enabled: enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractors", contractor.id] });
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      alert(Object.values(apiErrors)[0]?.[0] ?? "Nie udało się zapisać zmiany.");
    },
  });

  return (
    <section className="bg-white dark:bg-gray-800 p-5 mb-6">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        Powiadomienia mailowe o zamówieniach
      </h2>
      <label className="flex items-start gap-3 select-none">
        <input
          type="checkbox"
          checked={contractor.order_mail_enabled}
          disabled={!canManage || mutation.isPending}
          onChange={() => mutation.mutate(!contractor.order_mail_enabled)}
          className="mt-0.5 w-4 h-4 accent-[var(--color-primary)] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">
          Wysyłaj maile o nowym zamówieniu i o zmianie zamówienia
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Maile trafiają wyłącznie do kont tej firmy z zaznaczoną „Widocznością stawek".
            Kopia idzie do koordynatorów przedsięwzięcia.
            {!canManage && " Zmienia osoba z uprawnieniem do podwykonawców."}
          </span>
        </span>
      </label>
    </section>
  );
}

function UsersSection({
  contractorId,
  flow,
}: {
  contractorId: string;
  /** Skąd tu przyszliśmy — np. z prośby o konto, którą trzeba domknąć po założeniu konta. */
  flow: ContractorFlowState;
}) {
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  // Only technical directors and the CEO may toggle per-account flags
  // (SMS notifications, rate visibility)
  const canManageUserFlags = canManageContractors(authUser);
  const decideAccountRequest = useDecideAccountRequest();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<AddUserForm>(emptyAddUserForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // Skąd wzięło się hasło w ramce — założenie konta czy reset. Sam `isSuccess`
  // mutacji resetu zostaje na true i po kolejnym założeniu konta kłamałby.
  const [passwordFromReset, setPasswordFromReset] = useState(false);

  const { data: usersResponse, isLoading } = useQuery({
    queryKey: ["contractor-users", contractorId],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorUser[]; meta: { led_projects_count?: number } }>(
          `/contractors/${contractorId}/users/`,
        )
        .then((r) => r.data),
  });
  const users = usersResponse?.data;
  // „Projekty" ma sens tylko u firmy, która dziś prowadzi choć jeden projekt ZE.
  const leadsProjects = (usersResponse?.meta?.led_projects_count ?? 0) > 0;

  const createMutation = useMutation({
    mutationFn: (payload: AddUserForm) =>
      apiClient
        .post<{ data: { generated_password: string } & ContractorUser }>(
          `/contractors/${contractorId}/users/`,
          payload
        )
        .then((r) => r.data.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
      setGeneratedPassword(data.generated_password);
      setPasswordFromReset(false);
      setShowAdd(false);
      setForm(emptyAddUserForm());
      setFormErrors({});
      // Zgłoszenie z kolejki „Prośby o konto" zamyka się samo — bez tego ktoś
      // musiałby wrócić na tamten ekran i odklikać to drugi raz. Notatki nie
      // ruszamy: nie znamy jej z tego ekranu.
      if (flow.accountRequestId) {
        decideAccountRequest.mutate({
          id: flow.accountRequestId,
          status: "account_created",
        });
      }
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const flat: Record<string, string> = {};
      for (const [k, v] of Object.entries(apiErrors)) {
        flat[k] = Array.isArray(v) ? v[0] : String(v);
      }
      setFormErrors(flat);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) =>
      apiClient.delete(`/contractors/${contractorId}/users/${userId}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
    },
  });

  // Hasła nie da się odczytać — zgubione zastępuje się nowym. API to potrafiło od
  // początku, brakowało tylko przycisku, więc jedyną drogą było wejście do panelu
  // kont w Wsparciu.
  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) =>
      apiClient
        .patch<{ data: { generated_password: string } }>(
          `/contractors/${contractorId}/users/${userId}/`,
          { reset_password: true },
        )
        .then((r) => r.data.data),
    onSuccess: (data) => {
      setGeneratedPassword(data.generated_password);
      setPasswordFromReset(true);
      setShowPassword(false);
    },
    onError: () => alert("Nie udało się zresetować hasła."),
  });

  const resetPassword = (u: ContractorUser) => {
    if (
      confirm(
        `Wylosować nowe hasło dla konta ${u.username}? Stare przestanie działać ` +
          "natychmiast, a nowe pokaże się tylko raz.",
      )
    ) {
      resetPasswordMutation.mutate(u.id);
    }
  };

  const smsToggleMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      apiClient.patch(`/contractors/${contractorId}/users/${userId}/`, {
        sms_notifications_enabled: enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const msg = Object.values(apiErrors)[0]?.[0] ?? "Nie udało się zapisać zmiany.";
      alert(msg);
    },
  });

  const smsCheckboxTitle = (u: ContractorUser) => {
    if (!u.phone_number) return "Użytkownik nie ma podanego numeru telefonu";
    if (!canManageUserFlags)
      return "Powiadomienia SMS zmienia osoba z uprawnieniem do podwykonawców";
    return "Powiadomienia SMS o nowych zdarzeniach (max 1 SMS dziennie)";
  };

  const renderSmsCheckbox = (u: ContractorUser) => (
    <input
      type="checkbox"
      checked={u.sms_notifications_enabled}
      disabled={!canManageUserFlags || !u.phone_number || smsToggleMutation.isPending}
      onChange={() =>
        smsToggleMutation.mutate({ userId: u.id, enabled: !u.sms_notifications_enabled })
      }
      title={smsCheckboxTitle(u)}
      className="w-4 h-4 accent-[var(--color-primary)] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
    />
  );

  const ratesToggleMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      apiClient.patch(`/contractors/${contractorId}/users/${userId}/`, {
        can_see_rates: enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const msg = Object.values(apiErrors)[0]?.[0] ?? "Nie udało się zapisać zmiany.";
      alert(msg);
    },
  });

  const renderRatesCheckbox = (u: ContractorUser) => (
    <input
      type="checkbox"
      checked={u.can_see_rates}
      disabled={!canManageUserFlags || ratesToggleMutation.isPending}
      onChange={() => ratesToggleMutation.mutate({ userId: u.id, enabled: !u.can_see_rates })}
      title={
        canManageUserFlags
          ? "Konto firmy: widzi stawki na zamówieniach i umowach swojej firmy, dostaje maile o zamówieniach i zgłasza urlopy firmy w portalu"
          : "Widoczność stawek zmienia osoba z uprawnieniem do podwykonawców"
      }
      className="w-4 h-4 accent-[var(--color-primary)] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
    />
  );

  const rfqToggleMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      apiClient.patch(`/contractors/${contractorId}/users/${userId}/`, {
        can_access_rfq: enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const msg = Object.values(apiErrors)[0]?.[0] ?? "Nie udało się zapisać zmiany.";
      alert(msg);
    },
  });

  const renderRfqCheckbox = (u: ContractorUser) => (
    <input
      type="checkbox"
      checked={u.can_access_rfq}
      disabled={!canManageUserFlags || rfqToggleMutation.isPending}
      onChange={() => rfqToggleMutation.mutate({ userId: u.id, enabled: !u.can_access_rfq })}
      title={
        canManageUserFlags
          ? "Konto może przeglądać postępowania ofertowe i składać oferty w imieniu firmy"
          : "Dostęp do postępowań nadaje osoba z uprawnieniem do podwykonawców"
      }
      className="w-4 h-4 accent-[var(--color-primary)] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
    />
  );

  const ledToggleMutation = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      apiClient.patch(`/contractors/${contractorId}/users/${userId}/`, {
        can_access_led_projects: enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractor-users", contractorId] });
    },
    onError: (err: { response?: { data?: { errors?: Record<string, string[]> } } }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const msg = Object.values(apiErrors)[0]?.[0] ?? "Nie udało się zapisać zmiany.";
      alert(msg);
    },
  });

  const renderLedCheckbox = (u: ContractorUser) => (
    <input
      type="checkbox"
      checked={u.can_access_led_projects}
      disabled={!canManageUserFlags || ledToggleMutation.isPending}
      onChange={() =>
        ledToggleMutation.mutate({ userId: u.id, enabled: !u.can_access_led_projects })
      }
      title={
        canManageUserFlags
          ? "Konto widzi „Prowadzone projekty” — projekty ZE powierzone firmie do prowadzenia"
          : "Dostęp do prowadzonych projektów nadaje osoba z uprawnieniem do podwykonawców"
      }
      className="w-4 h-4 accent-[var(--color-primary)] disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
    />
  );

  const validateForm = () => {
    const errs: Record<string, string> = {};
    if (!form.first_name.trim()) errs.first_name = "Imię jest wymagane";
    if (!form.last_name.trim()) errs.last_name = "Nazwisko jest wymagane";
    if (!form.email.trim()) errs.email = "E-mail jest wymagany";
    if (!form.phone_number.trim()) errs.phone_number = "Numer telefonu jest wymagany";
    if (!form.code.trim() || form.code.length !== 3) errs.code = "Kod musi mieć dokładnie 3 litery";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const upd = <K extends keyof AddUserForm>(k: K) => (v: AddUserForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // Wejście z prośby o konto: formularz otwiera się od razu, wypełniony tym, co firma
  // sama o sobie napisała. Kod (3 litery) i podział imienia zostają do sprawdzenia.
  const prefillApplied = useRef(false);
  useEffect(() => {
    const prefill = flow.openUserForm;
    if (prefillApplied.current || !prefill) return;
    prefillApplied.current = true;
    setForm((prev) => ({ ...prev, ...prefill }));
    setShowAdd(true);
  }, [flow]);

  return (
    <section className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Konta użytkowników ({(users ?? []).length})
        </h2>
        <button
          className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
          onClick={() => { setShowAdd(true); setGeneratedPassword(null); }}
        >
          <Plus size={14} /> Dodaj konto
        </button>
      </div>

      {generatedPassword && (
        <div className="mb-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
          <p className="font-medium text-green-800 mb-1">
            {passwordFromReset
              ? "Nowe hasło dostępu (widoczne tylko teraz):"
              : "Konto utworzone. Hasło dostępu:"}
          </p>
          <div className="flex items-center gap-2">
            <code className="font-mono bg-white dark:bg-gray-800 border border-green-200 rounded px-2 py-1 text-green-900 select-all">
              {showPassword ? generatedPassword : "•".repeat(generatedPassword.length)}
            </code>
            <button
              onClick={() => setShowPassword((v) => !v)}
              className="text-green-700 hover:text-green-900"
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
            <button
              className="text-xs text-green-700 hover:underline"
              onClick={() => setGeneratedPassword(null)}
            >
              Zamknij
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="mb-4 bg-white dark:bg-gray-800 p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Nowe konto podwykonawcy</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {([
              { key: "first_name", label: "Imię", required: true },
              { key: "last_name", label: "Nazwisko", required: true },
              { key: "email", label: "E-mail", required: true },
              { key: "phone_number", label: "Telefon", required: true },
            ] as { key: AddUserTextField; label: string; required: boolean }[]).map(({ key, label, required }) => (
              <div key={key}>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input
                  type={key === "email" ? "email" : "text"}
                  value={form[key]}
                  onChange={(e) => upd(key)(e.target.value)}
                  className={`block w-full rounded-md border px-3 py-1.5 text-sm ${formErrors[key] ? "border-red-400" : "border-gray-300"}`}
                />
                {formErrors[key] && <p className="text-xs text-red-600 mt-0.5">{formErrors[key]}</p>}
              </div>
            ))}
          </div>
          <div className="mb-3 max-w-[180px]">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kod (3 litery)<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => upd("code")(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
              placeholder="ABC"
              className={`block w-full rounded-md border px-3 py-1.5 text-sm font-mono uppercase ${formErrors.code ? "border-red-400" : "border-gray-300"}`}
            />
            {formErrors.code && <p className="text-xs text-red-600 mt-0.5">{formErrors.code}</p>}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Login = imię_nazwisko_KOD (3 litery). Hasło losowe.</p>
          </div>
          <div className="mb-3 space-y-1.5">
            <label className="flex items-center gap-2 select-none w-fit">
              <input
                type="checkbox"
                checked={form.sms_notifications_enabled}
                onChange={(e) => upd("sms_notifications_enabled")(e.target.checked)}
                className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer"
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">
                Powiadomienia SMS
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                — max 1 SMS dziennie o nowych zdarzeniach
              </span>
            </label>
            <label className="flex items-center gap-2 select-none w-fit">
              <input
                type="checkbox"
                checked={form.can_see_rates}
                onChange={(e) => upd("can_see_rates")(e.target.checked)}
                className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer"
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">
                Widoczność stawek
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                — widzi stawki na zamówieniach i umowach; bez tego nie wystawi faktury
              </span>
            </label>
            <label className="flex items-center gap-2 select-none w-fit">
              <input
                type="checkbox"
                checked={form.can_access_rfq}
                onChange={(e) => upd("can_access_rfq")(e.target.checked)}
                className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer"
              />
              <span className="text-xs text-gray-700 dark:text-gray-300">
                Postępowania ofertowe
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                — może składać oferty w imieniu firmy
              </span>
            </label>
            {leadsProjects && (
              <label className="flex items-center gap-2 select-none w-fit">
                <input
                  type="checkbox"
                  checked={form.can_access_led_projects}
                  onChange={(e) => upd("can_access_led_projects")(e.target.checked)}
                  className="w-4 h-4 accent-[var(--color-primary)] cursor-pointer"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  Prowadzone projekty
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  — widzi projekty ZE powierzone firmie do prowadzenia
                </span>
              </label>
            )}
          </div>
          {createMutation.isError && Object.keys(formErrors).length === 0 && (
            <p className="text-xs text-red-600 mb-2">Błąd zapisu. Sprawdź dane.</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" loading={createMutation.isPending} onClick={() => { if (validateForm()) createMutation.mutate(form); }}>
              Utwórz konto
            </Button>
            <Button size="sm" variant="secondary" onClick={() => { setShowAdd(false); setForm(emptyAddUserForm()); setFormErrors({}); }}>
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {/* „Widoczność stawek" decyduje też o tym, kto dostaje maile o zamówieniach.
          Bez ani jednego takiego konta podwykonawca nie dostanie żadnego maila —
          dokładnie ta cisza, na którą skarżyli się podwykonawcy. */}
      {!isLoading &&
        (users ?? []).length > 0 &&
        !(users ?? []).some((u) => u.is_active && u.can_see_rates && u.email) && (
          <div className="mb-3 flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Żadne konto nie odbiera powiadomień mailowych o zamówieniach — maile trafiają
              wyłącznie do kont z zaznaczoną „Widocznością stawek" i uzupełnionym adresem e-mail.
              Te same konta (z „Widocznością stawek") zgłaszają urlopy firmy w portalu.
            </p>
          </div>
        )}

      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie...</p>
      ) : (users ?? []).length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak kont użytkowników.</p>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
            {(users ?? []).map((u) => {
              return (
                <div key={u.id}>
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {u.first_name} {u.last_name}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge color={u.is_active ? "green" : "gray"}>
                          {u.is_active ? "Aktywny" : "Nieaktywny"}
                        </Badge>
                        <button
                          className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-40"
                          disabled={resetPasswordMutation.isPending}
                          onClick={() => resetPassword(u)}
                          title="Zresetuj hasło (wylosuj nowe)"
                        >
                          <KeyRound size={15} />
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700 disabled:opacity-40"
                          disabled={deleteMutation.isPending}
                          onClick={() => {
                            if (confirm(`Usunąć konto ${u.first_name} ${u.last_name}?`)) {
                              deleteMutation.mutate(u.id);
                            }
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                    <div className="ml-4 mt-1 space-y-0.5">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{u.email || "—"}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{formatPhone(u.phone_number)}</p>
                      <p className="text-xs font-mono text-gray-400 dark:text-gray-500">{u.username}</p>
                      <label className="flex items-center gap-2 pt-1 select-none">
                        {renderSmsCheckbox(u)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">Powiadomienia SMS</span>
                      </label>
                      <label className="flex items-center gap-2 pt-1 select-none">
                        {renderRatesCheckbox(u)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">Widoczność stawek</span>
                      </label>
                      <label className="flex items-center gap-2 pt-1 select-none">
                        {renderRfqCheckbox(u)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">Postępowania ofertowe</span>
                      </label>
                      {leadsProjects && (
                        <label className="flex items-center gap-2 pt-1 select-none">
                          {renderLedCheckbox(u)}
                          <span className="text-xs text-gray-500 dark:text-gray-400">Prowadzone projekty</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Imię i nazwisko</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Login</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">E-mail</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Telefon</th>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">SMS</th>
                  <th
                    className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    title="Konto firmy: widoczność stawek na zamówieniach i umowach, maile o zamówieniach, faktury i zgłaszanie urlopów firmy w portalu"
                  >
                    Stawki
                  </th>
                  <th
                    className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                    title="Dostęp do postępowań ofertowych GK ELEKTIM i prawo składania ofert w imieniu firmy"
                  >
                    Oferty
                  </th>
                  {leadsProjects && (
                    <th
                      className="text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                      title="Dostęp do widoku „Prowadzone projekty” — projekty ZE powierzone firmie do prowadzenia"
                    >
                      Projekty
                    </th>
                  )}
                  <th className="px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                </tr>
              </thead>
              <tbody>
                {(users ?? []).map((u) => {
                  return (
                    <>
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">
                          {u.first_name} {u.last_name}
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{u.username}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{u.email || "—"}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatPhone(u.phone_number)}</td>
                        <td className="px-3 py-2">
                          <Badge color={u.is_active ? "green" : "gray"}>
                            {u.is_active ? "Aktywny" : "Nieaktywny"}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center">{renderSmsCheckbox(u)}</td>
                        <td className="px-3 py-2 text-center">{renderRatesCheckbox(u)}</td>
                        <td className="px-3 py-2 text-center">{renderRfqCheckbox(u)}</td>
                        {leadsProjects && (
                          <td className="px-3 py-2 text-center">{renderLedCheckbox(u)}</td>
                        )}
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-40 mr-2"
                            disabled={resetPasswordMutation.isPending}
                            onClick={() => resetPassword(u)}
                            title="Zresetuj hasło (wylosuj nowe)"
                          >
                            <KeyRound size={15} />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700 disabled:opacity-40"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (confirm(`Usunąć konto ${u.first_name} ${u.last_name}?`)) {
                                deleteMutation.mutate(u.id);
                              }
                            }}
                            title="Usuń konto"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export function ContractorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const qc = useQueryClient();
  // Z czym tu przyszliśmy (np. z prośby o konto). Ref, bo stan nawigacji ma zadziałać
  // raz — przeładowanie strony ma pokazać zwykłą kartę firmy.
  const flow = useRef<ContractorFlowState>(
    (location.state as ContractorFlowState | null) ?? {},
  ).current;
  const user = useAuthStore((s) => s.user);
  const hasDlc01 = (user?.accessible_dlc_modules ?? []).includes("dlc_01");
  // Przycisk „Mapa zamówień (podgląd)" ma sens tylko, gdy firma COKOLWIEK na niej ma
  // (żywe zlecenie WPW/MDCP albo aktywne „planowane") — tani ?check=1 na backendzie.
  const { data: hasMapProjects } = useQuery({
    queryKey: ["pp", "contractor-map-check", id],
    enabled: hasDlc01 && !!id,
    queryFn: () =>
      apiClient
        .get<{ data: { has_projects: boolean } }>("/power-projects/contractor/megamap/", {
          params: { as_contractor: id, check: 1 },
        })
        .then((r) => r.data.data.has_projects),
  });
  // CEO / technical director by role, or the CEO-granted 'Podwykonawcy i umowy
  // ramowe' permission (mirrors backend CanManageContractors). `isDirector` kept
  // as the name the rest of this page already uses for "may edit".
  const isDirector = canManageContractors(user);
  const canManageDoc = isDirector;
  // Podwykonawców się nie usuwa — tylko dezaktywuje; włącza i wyłącza CEO albo
  // dyrektor techniczny (backend: IsTechnicalDirector).
  const canToggleActive = canToggleContractorActive(user);
  const [confirmToggle, setConfirmToggle] = useState(false);

  const toggleActiveMutation = useMutation({
    // Sam is_active, nie cały formularz: dezaktywacja nie może się wywrócić na starym
    // błędzie w innym polu karty (np. sumie kontrolnej REGON).
    mutationFn: (isActive: boolean) =>
      apiClient.patch(`/contractors/${id}/`, { is_active: isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contractors"] });
      setConfirmToggle(false);
    },
  });

  const { data: contractor, isLoading } = useQuery({
    queryKey: ["contractors", id],
    queryFn: () =>
      apiClient
        .get<{ data: Contractor[] }>("/contractors/", { params: { contractor: id } })
        .then((r) => r.data.data[0] ?? null),
    enabled: !!id,
  });
  usePageTitle(contractor && getContractorDisplayName(contractor));

  const { data: orders } = useQuery({
    queryKey: ["orders", { contractor: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Order[] }>("/orders/", { params: { contractor: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: protocols } = useQuery({
    queryKey: ["protocols", { contractor: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Protocol[] }>("/protocols/", { params: { contractor: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: agreements } = useQuery({
    queryKey: ["agreements", { contractor: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement[] }>("/agreements/", { params: { contractor: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: invoices } = useQuery({
    queryKey: ["invoices", { contractor: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Invoice[] }>("/invoices/", { params: { contractor: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  if (isLoading) return <PageLoader />;
  if (!contractor) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono podwykonawcy.</p>;

  // Kwoty NETTO zamówień i protokołów POCK. Granicą uprawnień jest serwer: rate_visibility
  // zeruje kwoty pozycji, których ten użytkownik nie może widzieć, więc sumujemy wyłącznie
  // to, co API ujawniło. Gdy nic nie jest widoczne, kolumny z kwotami w ogóle się nie
  // pokazują — pusta kolumna obiecywałaby dane, do których nie ma dostępu.
  const settledByOrder = new Map<string, { value: number; masked: number }>();
  for (const p of (protocols ?? []).filter(isSettledProtocol)) {
    const { value, masked } = protocolNetValue(p);
    const acc = settledByOrder.get(p.order) ?? { value: 0, masked: 0 };
    settledByOrder.set(p.order, { value: acc.value + value, masked: acc.masked + masked });
  }

  /** Pozostało do rozliczenia = wartość zamówienia − rozliczone protokołami POCK.
   *  null, gdy choć jedna pozycja zamówienia lub POCK-a jest dla nas zamaskowana —
   *  różnica z niepełnych danych zawyżałaby kwotę, a tego nikt by nie zauważył. */
  const remainingToSettle = (o: Order): number | null => {
    const values = (o.line_items ?? []).map((li) => li.total_value);
    if (values.length === 0 || values.some((v) => v == null)) return null;
    const settled = settledByOrder.get(o.id) ?? { value: 0, masked: 0 };
    if (settled.masked > 0) return null;
    const total = values.reduce((sum, v) => sum + parseFloat(v as string), 0);
    return total - settled.value;
  };

  // Zwykłe funkcje renderujące, nie komponenty — komponent zdefiniowany w ciele innego
  // komponentu montowałby się od nowa przy każdym renderze.
  const remainingCell = (order: Order) => {
    const remaining = remainingToSettle(order);
    if (remaining === null) {
      return (
        <span
          className="text-gray-300 dark:text-gray-600"
          title="Brak pełnych uprawnień do kwot tego zamówienia"
        >
          —
        </span>
      );
    }
    return (
      <span className={`tabular-nums whitespace-nowrap ${remaining < 0 ? "text-red-600 dark:text-red-400" : ""}`}>
        {formatAmount(remaining)} PLN
      </span>
    );
  };

  /** Wartość netto protokołu POCK — suma pozycji widocznych dla tego użytkownika;
   *  „*" gdy część pozycji jest zamaskowana, „—" gdy żadna nie jest widoczna. */
  const protocolAmountCell = (protocol: Protocol) => {
    const { value, masked } = protocolNetValue(protocol);
    if ((protocol.items ?? []).length - masked <= 0) {
      return <span className="text-gray-300 dark:text-gray-600">—</span>;
    }
    return (
      <span
        className="tabular-nums whitespace-nowrap"
        title={masked > 0 ? "Suma tylko z pozycji, do których masz uprawnienia finansowe" : undefined}
      >
        {formatAmount(value)} PLN
        {masked > 0 && <span className="text-gray-400 dark:text-gray-500">*</span>}
      </span>
    );
  };

  const showOrderAmounts = (orders ?? []).some((o) =>
    (o.line_items ?? []).some((li) => li.total_value != null),
  );
  const showProtocolAmounts = (protocols ?? []).some((p) =>
    (p.items ?? []).some((i) => i.amount_net != null || i.order_line_item_unit_rate != null),
  );

  return (
    <div className="max-w-6xl">
      <Link
        to="/contractors"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> Podwykonawcy
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{getContractorDisplayName(contractor)}</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-2" />
          {contractor.short_name && (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">{contractor.short_name}</p>
          )}
          {contractor.entity_type === "individual" && (
            <span className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700/50 text-[11px] font-medium uppercase tracking-wider text-purple-700 dark:text-purple-300">
              <User size={12} strokeWidth={2.25} /> Osoba fizyczna · UZ/UoD
            </span>
          )}
        </div>
        {/* Prawa strona nagłówka w dwóch RZĘDACH (statusy / akcje) — jeden wrapowany
            rząd łamał się w przypadkowych miejscach przy dłuższych nazwach firm. */}
        <div className="flex flex-col items-start gap-2 sm:items-end">
          <div className="flex flex-wrap items-center gap-2">
            {contractor.entity_type === "company" && contractor.is_vat_payer && <Badge color="blue">Płatnik VAT</Badge>}
            {contractor.entity_type === "individual" && <Badge color="purple">Rachunek</Badge>}
            <Badge color={contractor.is_active ? "green" : "gray"}>
              {contractor.is_active ? "Aktywny" : "Nieaktywny"}
            </Badge>
            {contractor.on_leave && (
              <Badge
                color="orange"
                title="Urlop firmy w oknie składania zamówienia — na listach i w podpowiedzi stoi na dole z tym dopiskiem"
              >
                {onLeaveLabel(contractor.on_leave)}
              </Badge>
            )}
          </div>
          {(contractor.work_types ?? []).length > 0 && (
            <div className="flex flex-wrap items-center gap-1" data-testid="contractor-work-types">
              {(contractor.work_types ?? []).map((t) => (
                <span
                  key={t}
                  className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${WORK_TYPE_BADGE[t]}`}
                  title="Typ prowadzonych prac (edycja firmy → Typy prowadzonych prac)"
                >
                  {WORK_TYPE_LABELS[t]}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
          {hasDlc01 && hasMapProjects && (
            <Link
              to={`/power-projects/contractor-preview/${id}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              title="Zobacz dokładnie tę mapę zamówień, którą ta firma widzi w swoim panelu (te same dane, te same eksporty)"
            >
              <Eye size={13} /> Mapa zamówień (podgląd)
            </Link>
          )}
          {isDirector && (
            <>
              <Link
                to={`/contractors/${id}/edit`}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <Pencil size={13} /> Edytuj
              </Link>
            </>
          )}
          {canToggleActive && (
            <button
              onClick={() => {
                toggleActiveMutation.reset();
                setConfirmToggle(true);
              }}
              className={
                contractor.is_active
                  ? "inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  : "inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
              }
            >
              {contractor.is_active ? (
                <>
                  <PowerOff size={13} /> Dezaktywuj
                </>
              ) : (
                <>
                  <Power size={13} /> Aktywuj
                </>
              )}
            </button>
          )}
          </div>
        </div>
      </div>

      {!contractor.is_active && (
        <div className="mb-6 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
          <span className="font-medium">Podwykonawca nieaktywny.</span> Nie pojawia się na liście ani w wyborze
          przy nowych zamówieniach i zapotrzebowaniach.
          {canToggleActive
            ? " Możesz go aktywować ponownie przyciskiem „Aktywuj”."
            : " Aktywować go ponownie może dyrektor techniczny lub CEO."}
        </div>
      )}

      {/* Details */}
      <div className="bg-white dark:bg-gray-800 p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {contractor.entity_type === "individual" ? "Dane osoby fizycznej" : "Dane formalne"}
        </h2>
        <div className="flex flex-col gap-2">
          {contractor.entity_type === "individual" ? (
            <>
              <InfoRow label="Imię" value={contractor.first_name ?? ""} />
              <InfoRow label="Nazwisko" value={contractor.last_name ?? ""} />
              <InfoRow label="PESEL" value={contractor.pesel ?? ""} />
            </>
          ) : (
            <>
              <InfoRow label="NIP" value={contractor.nip} />
              <InfoRow label="REGON" value={contractor.regon} />
              <InfoRow label="KRS" value={contractor.krs} />
              <InfoRow label="Nr VAT UE" value={contractor.eu_vat_number} />
            </>
          )}
          <InfoRow label="Adres rejestrowy" value={formatAddress(contractor.registered_address)} />
          {contractor.correspondence_address && (
            <InfoRow label="Adres korespondencyjny" value={formatAddress(contractor.correspondence_address)} />
          )}
          <InfoRow label="Konto bankowe" value={contractor.bank_account_number} />
          <InfoRow label="Bank" value={contractor.bank_name} />
          <InfoRow label="Osoba kontaktowa" value={contractor.contact_person} />
          <InfoRow label="E-mail" value={contractor.contact_email} />
          <InfoRow label="Telefon" value={formatPhone(contractor.contact_phone)} />
          <InfoRow label="Strona WWW" value={contractor.website} />
          {contractor.notes && <InfoRow label="Uwagi" value={contractor.notes} />}
        </div>
      </div>

      {/* Zakres współpracy — co firma robi i gdzie; podstawa dopuszczeń do postępowań */}
      {id && <CooperationScopeSection contractorId={id} />}

      {/* Urlopy firmy — zgłasza konto firmy (z widocznością stawek) w portalu; GK czyta, osoba z uprawnieniem
          do podwykonawców może też dopisać / skrócić / wycofać (np. zgłoszenie telefoniczne). */}
      {id && <ContractorLeavesSection contractorId={id} canManage={isDirector} variant="card" />}

      {/* Terminy (TODO) firmy — podgląd „oczami podwykonawcy": ten sam payload,
          który firma widzi w portalu (parytet pilnuje test backendu). Bramka
          lustrzana do backendowego IsTechnicalDirector (dyr. tech. / CEO).
          Zwinięcie to preferencja przeglądarki (localStorage) i wyłącza fetch. */}
      {id && (user?.is_ceo || (user?.role === "director" && user?.is_technical_director)) && (
        <TodoPreviewSection contractorId={id} />
      )}

      {/* Oceny współpracy — po zrealizowanych zamówieniach, wyłącznie dla GK */}
      {id && <ContractorRatingsSection contractorId={id} />}

      {/* Zasady podpowiadania — wskazówki dla silnika podpowiedzi firmy przy zlecaniu */}
      {id && <ContractorKnowledgeNotesSection contractorId={id} />}

      {/* Historia karty — tylko dla wewnętrznych; API i tak odcina podwykonawców */}
      {id && <ContractorHistorySection contractorId={id} />}

      {/* Users — Director only */}
      {isDirector && <OrderMailToggle contractor={contractor} />}

      {isDirector && id && <UsersSection contractorId={id} flow={flow} />}

      {/* Framework agreements */}
      <section className="mb-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Umowy ramowe ({(agreements ?? []).length})
          </h2>
          <Link
            to="/agreements"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] hover:underline whitespace-nowrap"
          >
            Wszystkie umowy ramowe
          </Link>
        </div>
        {(agreements ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak umów ramowych.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {(agreements ?? []).map((a) => {
                const expired = isAgreementExpired(a.valid_until);
                return (
                  <div key={a.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/agreements/${a.id}`}
                        className="font-medium text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 min-w-0"
                      >
                        <span className="truncate">{a.title}</span> <ExternalLink size={11} className="shrink-0" />
                      </Link>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge color={expired ? "red" : "green"}>{expired ? "Wygasła" : "Aktywna"}</Badge>
                        {a.is_confidential && isDirector && (
                          <span title="Umowa poufna — ukryta przed pozostałymi pracownikami. Ty ją widzisz i możesz pobrać jej plik.">
                            <Badge color="gray">Poufna</Badge>
                          </span>
                        )}
                        {canManageDoc && a.requires_insurance && (a.insurance_status === "missing" || a.insurance_status === "expired") && (
                          <Badge color="red">Brak OC</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(a.signed_date).toLocaleDateString("pl")}
                      {a.valid_until ? ` → ${new Date(a.valid_until).toLocaleDateString("pl")}` : ""}
                      {" · "}
                      {a.line_items_count} poz.
                    </p>
                    {canManageDoc && (
                      <div className="mt-1">
                        {a.document_managed_file_detail ? (
                          <ManagedFileLink
                            file={a.document_managed_file_detail}
                            icon={<FileText size={11} />}
                          >
                            Pobierz umowę
                          </ManagedFileLink>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            Brak skanu umowy
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[560px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Tytuł umowy</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data podpisania</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ważna do</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycje</th>
                    {canManageDoc && (
                      <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Plik umowy</th>
                    )}
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(agreements ?? []).map((a) => {
                    const expired = isAgreementExpired(a.valid_until);
                    return (
                      <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2">
                          <Link
                            to={`/agreements/${a.id}`}
                            className="font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                          >
                            {a.title} <ExternalLink size={11} className="text-gray-400 dark:text-gray-500" />
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {new Date(a.signed_date).toLocaleDateString("pl")}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {a.valid_until ? new Date(a.valid_until).toLocaleDateString("pl") : "—"}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{a.line_items_count}</td>
                        {canManageDoc && (
                          <td className="px-3 py-2 whitespace-nowrap">
                            {a.document_managed_file_detail ? (
                              <ManagedFileLink
                                file={a.document_managed_file_detail}
                                icon={<FileText size={11} />}
                              >
                                Pobierz
                              </ManagedFileLink>
                            ) : (
                              <span
                                className="text-gray-400 dark:text-gray-500"
                                title="Nikt nie wgrał jeszcze skanu tej umowy"
                              >
                                brak skanu
                              </span>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge color={expired ? "red" : "green"}>{expired ? "Wygasła" : "Aktywna"}</Badge>
                            {a.is_confidential && isDirector && (
                              <span title="Umowa poufna — ukryta przed pozostałymi pracownikami. Ty ją widzisz i możesz pobrać jej plik.">
                                <Badge color="gray">Poufna</Badge>
                              </span>
                            )}
                            {canManageDoc && a.requires_insurance && a.insurance_status === "missing" && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full px-1.5 py-0.5" title="Brak polisy OC"><ShieldOff size={10} /> Brak OC</span>
                            )}
                            {canManageDoc && a.requires_insurance && a.insurance_status === "expired" && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full px-1.5 py-0.5" title="Polisa OC wygasła"><ShieldAlert size={10} /> OC wygasła</span>
                            )}
                            {canManageDoc && a.requires_insurance && a.insurance_status === "expiring_soon" && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full px-1.5 py-0.5" title="Polisa OC wygasa wkrótce"><ShieldAlert size={10} /> OC wkrótce</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Orders */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Zamówienia ({(orders ?? []).length})
        </h2>
        {(orders ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak zamówień.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {(orders ?? []).map((o) => (
                <div key={o.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/orders/${o.id}`}
                      className="font-medium text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                    >
                      {o.order_number} <ExternalLink size={11} />
                    </Link>
                    <Badge color={orderStatusColor[o.status] ?? "gray"}>
                      {orderStatusLabel[o.status] ?? o.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{o.project_name ?? "—"}</p>
                  {showOrderAmounts && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Wartość: <VisibleAmountSum values={(o.line_items ?? []).map((li) => li.total_value)} />
                      {" · "}Pozostało: {remainingCell(o)}
                    </p>
                  )}
                  {o.deadline && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {new Date(o.deadline).toLocaleDateString("pl")}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[480px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr zamówienia</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Przedsięwzięcie</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    {showOrderAmounts && (
                      <>
                        <th
                          className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                          title="Wartość netto pozycji zamówienia — suma kwot widocznych dla Ciebie"
                        >
                          Wartość
                        </th>
                        <th
                          className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                          title="Wartość netto zamówienia pomniejszona o protokoły POCK (bez wycofanych i odrzuconych)"
                        >
                          Pozostało
                        </th>
                      </>
                    )}
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Termin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(orders ?? []).map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Link
                          to={`/orders/${o.id}`}
                          className="font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                        >
                          {o.order_number} <ExternalLink size={11} className="text-gray-400 dark:text-gray-500" />
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[300px] min-[1440px]:max-w-[360px] truncate" title={o.project_name ?? undefined}>{o.project_name ?? "—"}</td>
                      <td className="px-3 py-2">
                        <Badge color={orderStatusColor[o.status] ?? "gray"}>
                          {orderStatusLabel[o.status] ?? o.status}
                        </Badge>
                      </td>
                      {showOrderAmounts && (
                        <>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            <VisibleAmountSum values={(o.line_items ?? []).map((li) => li.total_value)} />
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {remainingCell(o)}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {o.deadline ? new Date(o.deadline).toLocaleDateString("pl") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Protocols */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Protokoły POCK ({(protocols ?? []).length})
        </h2>
        {(protocols ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak protokołów.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {(protocols ?? [])
                .sort((a, b) => (a.is_withdrawn === b.is_withdrawn ? 0 : a.is_withdrawn ? 1 : -1))
                .map((p) => (
                  <div key={p.id} className={`px-4 py-3 ${p.is_withdrawn ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/protocols/${p.id}`}
                        className={`font-medium text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${p.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                      >
                        {p.protocol_number} <ExternalLink size={11} />
                      </Link>
                      <div className="flex items-center gap-1 shrink-0">
                        {p.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : (
                          <Badge color={p.is_final ? "purple" : "blue"}>
                            {p.is_final ? "Końcowy" : "Częściowy"}
                          </Badge>
                        )}
                        {!p.is_withdrawn && (
                          <Badge
                            color={p.accepted_by_contractor === null ? "gray" : p.accepted_by_contractor ? "green" : "red"}
                          >
                            {p.accepted_by_contractor === null ? "Oczekuje" : p.accepted_by_contractor ? "Zaakceptowany" : "Odrzucony"}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {p.order_number} · {new Date(p.created_at).toLocaleDateString("pl")}
                    </p>
                    {showProtocolAmounts && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Wartość: {protocolAmountCell(p)}
                      </p>
                    )}
                  </div>
                ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr protokołu</th>
                    <th className="text-left px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówienie</th>
                    <th className="text-left px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typ</th>
                    <th className="text-left px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    {showProtocolAmounts && (
                      <th
                        className="text-right px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"
                        title="Wartość netto protokołu — suma kwot widocznych dla Ciebie"
                      >
                        Wartość
                      </th>
                    )}
                    <th className="text-left px-2 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(protocols ?? [])
                    .sort((a, b) => (a.is_withdrawn === b.is_withdrawn ? 0 : a.is_withdrawn ? 1 : -1))
                    .map((p) => (
                    <tr key={p.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${p.is_withdrawn ? "opacity-60" : ""}`}>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <Link
                          to={`/protocols/${p.id}`}
                          className={`font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${p.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                        >
                          {p.protocol_number} <ExternalLink size={11} className="text-gray-400 dark:text-gray-500" />
                        </Link>
                      </td>
                      <td className="px-2 py-2 text-gray-600 dark:text-gray-400 max-w-[220px] truncate" title={p.order_number}>{p.order_number}</td>
                      <td className="px-2 py-2">
                        {p.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : (
                          <Badge color={p.is_final ? "purple" : "blue"}>
                            {p.is_final ? "Końcowy" : "Częściowy"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {!p.is_withdrawn && (
                          <Badge
                            color={
                              p.accepted_by_contractor === null ? "gray"
                              : p.accepted_by_contractor ? "green" : "red"
                            }
                          >
                            {p.accepted_by_contractor === null
                              ? "Oczekuje"
                              : p.accepted_by_contractor
                              ? "Zaakceptowany"
                              : "Odrzucony"}
                          </Badge>
                        )}
                      </td>
                      {showProtocolAmounts && (
                        <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300">
                          {protocolAmountCell(p)}
                        </td>
                      )}
                      <td className="px-2 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(p.created_at).toLocaleDateString("pl")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Invoices */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Faktury P-GK ({(invoices ?? []).length})
        </h2>
        {(invoices ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak faktur.</p>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {(invoices ?? []).map((inv) => {
                const isWithdrawn = inv.status === "withdrawn";
                return (
                  <div key={inv.id} className={`px-4 py-3 ${isWithdrawn ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className={`font-medium text-sm text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${isWithdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                      >
                        {inv.invoice_number} <ExternalLink size={11} />
                      </Link>
                      <Badge color={invoiceStatusColor[inv.status] ?? "gray"}>
                        {invoiceStatusLabel[inv.status] ?? inv.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{inv.order_number ?? "—"}</p>
                    <p className="text-xs font-mono text-gray-700 dark:text-gray-300 mt-0.5">
                      {formatAmount(inv.amount_gross)} PLN brutto
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {new Date(inv.submitted_at).toLocaleDateString("pl")}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs min-w-[520px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nr faktury</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówienie</th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Netto</th>
                    <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Brutto</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(invoices ?? []).map((inv) => {
                    const isWithdrawn = inv.status === "withdrawn";
                    return (
                      <tr key={inv.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isWithdrawn ? "opacity-60" : ""}`}>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <Link
                            to={`/invoices/${inv.id}`}
                            className={`font-medium text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 ${isWithdrawn ? "line-through text-gray-400 dark:text-gray-500" : ""}`}
                          >
                            {inv.invoice_number} <ExternalLink size={11} className="text-gray-400 dark:text-gray-500" />
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">{inv.order_number ?? "—"}</td>
                        <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap font-mono">{formatAmount(inv.amount_net)} PLN</td>
                        <td className="px-3 py-2 text-right font-medium whitespace-nowrap font-mono">{formatAmount(inv.amount_gross)} PLN</td>
                        <td className="px-3 py-2">
                          <Badge color={invoiceStatusColor[inv.status] ?? "gray"}>
                            {invoiceStatusLabel[inv.status] ?? inv.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {new Date(inv.submitted_at).toLocaleDateString("pl")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Deactivate / reactivate confirmation modal */}
      {confirmToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            role="dialog"
            aria-label={contractor.is_active ? "Dezaktywuj podwykonawcę" : "Aktywuj podwykonawcę"}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm mx-4"
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {contractor.is_active ? "Dezaktywuj podwykonawcę" : "Aktywuj podwykonawcę"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              {contractor.is_active ? (
                <>
                  Podwykonawca <span className="font-medium text-gray-800 dark:text-gray-200">{getContractorDisplayName(contractor)}</span> przestanie
                  pojawiać się na liście i w wyborze przy nowych zamówieniach i zapotrzebowaniach. Karta, zamówienia
                  i faktury zostają — dyrektor techniczny lub CEO może go w każdej chwili aktywować ponownie.
                </>
              ) : (
                <>
                  Podwykonawca <span className="font-medium text-gray-800 dark:text-gray-200">{getContractorDisplayName(contractor)}</span> wróci
                  na listę i do wyboru przy nowych zamówieniach i zapotrzebowaniach.
                </>
              )}
            </p>
            {toggleActiveMutation.isError && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400 mb-3">
                {firstApiError(toggleActiveMutation.error, "Nie udało się zmienić statusu. Spróbuj ponownie.")}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmToggle(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Anuluj
              </button>
              <button
                onClick={() => toggleActiveMutation.mutate(!contractor.is_active)}
                disabled={toggleActiveMutation.isPending}
                className={
                  contractor.is_active
                    ? "px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    : "px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                }
              >
                {toggleActiveMutation.isPending
                  ? "Zapisywanie..."
                  : contractor.is_active
                    ? "Tak, dezaktywuj"
                    : "Tak, aktywuj"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
